import {
  Language,
  PLATFORM_TOUR_VALID_LANGUAGES,
} from '@openeducation/oe-rn-shared-types';

import PlatformTourDao from '../dao/platform-tour.dao';
import type { Environment } from '../interfaces/environment';
import type {
  PlatformTourReminder,
  PlatformTours,
} from '../interfaces/platform-tours';
import { dateArrayToUtc } from '../utils';

export abstract class PlatformTourService {
  static async getReservationByContactIdAndLanguage(
    env: Environment,
    timeZone: string,
    language: Language | null,
    contactId?: string
  ): Promise<Date | null> {
    if (!contactId || !language || !timeZone) {
      return Promise.reject(
        new Error(
          `Request contactID:${contactId}, language: ${language}, timeZone: ${timeZone}`
        )
      );
    }
    try {
      const response =
        await PlatformTourDao.getReservationByContactIdAndLanguage(
          env,
          contactId,
          language
        );
      return dateArrayToUtc(response.reservationDate, timeZone);
    } catch (error) {
      return Promise.reject(error);
    }
  }

  static async getNextPlatformTour(
    env: Environment,
    preferredLang: Language | null
  ): Promise<PlatformTours> {
    let language = preferredLang as Language;
    language = !PLATFORM_TOUR_VALID_LANGUAGES.includes(language)
      ? Language.ES
      : language;

    return !language
      ? Promise.reject(new Error(`Request language: ${language}`))
      : await PlatformTourDao.getNextPlatformTour(env, language);
  }

  static async getUpcomingPlatformTours(
    env: Environment,
    fromDate: Date,
    toDate: Date,
    language: Language | null
  ) {
    return !env || !language || !fromDate || !toDate
      ? Promise.reject(
          new Error(
            `Request env:${env}, language: ${language}, fromDate: ${fromDate}, toDate: ${toDate}`
          )
        )
      : await PlatformTourDao.getUpcomingPlatformTours(
          env,
          language,
          fromDate,
          toDate
        );
  }

  static async getUpcomingPlatformToursWithoutDuplicates(
    env: Environment,
    fromDate: Date,
    toDate: Date,
    language: Language | null
  ) {
    const withDups = await PlatformTourService.getUpcomingPlatformTours(
      env,
      fromDate,
      toDate,
      language
    );

    // We remove the duplicates based on the meetingLink and the startTime array ( [...new Set(withDups)] doesn't work)
    const noDups = withDups.filter(
      (pt, index, self) =>
        index ===
        self.findIndex(
          (t) =>
            t.meetingLink === pt.meetingLink &&
            t.startTime.every((value, index) => value === pt.startTime[index])
        )
    );

    return noDups;
  }

  static savePlatformTourReminder(
    env: Environment,
    body: PlatformTourReminder
  ): Promise<void> {
    return PlatformTourDao.savePlatformTourReminder(env, body);
  }
}
